import { useEffect, useMemo, useState } from "react";
import api, { API } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { BarChart3, CheckCircle2, XCircle, Eye, Users, FileText, Search, Download } from "lucide-react";
import MonthlyDocumentsPanel from "../../components/MonthlyDocumentsPanel";

export default function AdminProgress() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailUser, setDetailUser] = useState(null);
  const [detailAttempts, setDetailAttempts] = useState([]);
  const [attemptDetail, setAttemptDetail] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    api.get("/admin/progress").then((r) => setRows(r.data)).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.name, r.first_name, r.last_name, r.email, r.dni]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q))
    );
  }, [rows, search]);

  const totalUsers = rows.length;
  const totalAssigned = rows.reduce((s, r) => s + r.assigned, 0);
  const totalPassed = rows.reduce((s, r) => s + r.passed, 0);

  const openDetail = async (user) => {
    setDetailUser(user);
    const { data } = await api.get(`/admin/users/${user.user_id}/attempts`);
    setDetailAttempts(data);
  };

  const exportCsv = async () => {
    const token = localStorage.getItem("token");
    const res = await fetch(`${API}/admin/progress/export`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "progreso.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-progress-page">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl text-gray-900">Progreso</h1>
          <p className="text-sm text-gray-500 mt-1">
            Resumen del estado de todos los usuarios y sus tests asignados.
          </p>
        </div>
        <Button variant="outline" onClick={exportCsv} data-testid="export-csv">
          <Download className="w-4 h-4 mr-2" /> Exportar CSV
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="p-6 border border-gray-200 shadow-none rounded-lg">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">Personas usuarias</span>
            <Users className="w-4 h-4 text-gray-400" />
          </div>
          <p className="font-display text-4xl text-gray-900 mt-2">{totalUsers}</p>
        </Card>
        <Card className="p-6 border border-gray-200 shadow-none rounded-lg">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">Asignaciones</span>
            <FileText className="w-4 h-4 text-gray-400" />
          </div>
          <p className="font-display text-4xl text-gray-900 mt-2">{totalAssigned}</p>
        </Card>
        <Card className="p-6 border border-gray-200 shadow-none rounded-lg">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">Aprobados</span>
            <BarChart3 className="w-4 h-4 text-emerald-500" />
          </div>
          <p className="font-display text-4xl text-emerald-600 mt-2">{totalPassed}</p>
        </Card>
      </div>

      <MonthlyDocumentsPanel />

      <div className="mb-4 relative w-80">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por nombre, email, DNI/NIE/Pasaporte…"
          className="pl-9"
          data-testid="progress-search"
        />
      </div>

      <Card className="border border-gray-200 shadow-none rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Usuario</TableHead>
              <TableHead>DNI / NIE / Pasaporte</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Asignados</TableHead>
              <TableHead>Aprobados</TableHead>
              <TableHead className="text-right">Detalle</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-500">Cargando…</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-500">Sin resultados.</TableCell></TableRow>
            ) : (
              filtered.map((r) => (
                <TableRow key={r.user_id} data-testid={`progress-row-${r.email}`}>
                  <TableCell className="font-medium">
                    {r.first_name || r.last_name ? `${r.first_name || ""} ${r.last_name || ""}`.trim() : r.name}
                  </TableCell>
                  <TableCell className="font-mono-data text-sm">{r.dni || "—"}</TableCell>
                  <TableCell className="font-mono-data text-sm text-gray-600">{r.email}</TableCell>
                  <TableCell>{r.assigned}</TableCell>
                  <TableCell>
                    <span className="font-mono-data text-emerald-600">{r.passed}/{r.assigned}</span>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="outline" size="sm" onClick={() => openDetail(r)} data-testid={`view-${r.email}`}>
                      <Eye className="w-3.5 h-3.5 mr-1" /> Ver
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!detailUser} onOpenChange={() => setDetailUser(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Detalle: {detailUser?.first_name} {detailUser?.last_name}{" "}
              <span className="text-gray-400 font-normal text-sm">({detailUser?.email})</span>
            </DialogTitle>
          </DialogHeader>

          <h3 className="font-medium mt-2">Cursos asignados</h3>
          <div className="space-y-2">
            {detailUser?.items?.map((it) => (
              <div key={it.test_id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg bg-white">
                <div>
                  <p className="text-sm font-medium">{it.test_title}</p>
                  <p className="text-xs text-gray-500">
                    Intentos: {it.attempts} • Mejor: {it.best_score == null ? "—" : `${it.best_score}%`} • Objetivo: {it.pass_percentage}%
                  </p>
                </div>
                {it.passed ? (
                  <span className="inline-flex items-center gap-1 text-emerald-600 text-sm">
                    <CheckCircle2 className="w-4 h-4" /> Aprobado
                  </span>
                ) : it.attempts > 0 ? (
                  <span className="inline-flex items-center gap-1 text-red-500 text-sm">
                    <XCircle className="w-4 h-4" /> En progreso
                  </span>
                ) : (
                  <span className="text-gray-400 text-sm">Sin intentos</span>
                )}
              </div>
            ))}
            {detailUser?.items?.length === 0 && (
              <p className="text-sm text-gray-500">Sin tests asignados.</p>
            )}
          </div>

          <h3 className="font-medium mt-4">Historial de intentos</h3>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Test</TableHead>
                  <TableHead>Puntuación</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {detailAttempts.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center text-gray-500 py-4">Sin intentos.</TableCell></TableRow>
                ) : (
                  detailAttempts.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell className="text-sm">{a.test_title}</TableCell>
                      <TableCell className="font-mono-data">{a.score_percentage}%</TableCell>
                      <TableCell>
                        {a.passed ? (
                          <span className="text-emerald-600 text-sm">Aprobado</span>
                        ) : (
                          <span className="text-red-500 text-sm">Suspendido</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-gray-500">
                        {new Date(a.submitted_at).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => setAttemptDetail(a)}>
                          <Eye className="w-3.5 h-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!attemptDetail} onOpenChange={() => setAttemptDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalle del intento</DialogTitle>
          </DialogHeader>
          {attemptDetail && (
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50 border border-gray-200">
                <div>
                  <p className="text-sm font-medium">{attemptDetail.test_title}</p>
                  <p className="text-xs text-gray-500">
                    {attemptDetail.correct_count}/{attemptDetail.total_questions} correctas • Duración: {attemptDetail.duration_seconds}s
                  </p>
                </div>
                <span className={`font-display text-2xl ${attemptDetail.passed ? "text-emerald-600" : "text-red-500"}`}>
                  {attemptDetail.score_percentage}%
                </span>
              </div>
              {attemptDetail.breakdown?.map((b, i) => (
                <div key={i} className="p-3 border border-gray-200 rounded-lg">
                  <p className="text-sm font-medium">{i + 1}. {b.question_text}</p>
                  <p className="text-xs text-gray-600 mt-1">
                    Tu respuesta: <span className="font-mono-data">[{b.user_answer.join(",")}]</span> • Correcta: <span className="font-mono-data">[{b.correct_answer.join(",")}]</span>
                  </p>
                  {b.is_correct ? <span className="text-emerald-600 text-xs">Correcta</span> : <span className="text-red-500 text-xs">Incorrecta</span>}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
